import { CORE_CHAIN_TEST_ENTRIES, } from "./chain-matrix.js";
/** Pinned block-by-number vectors for chains with verified getBlockByNumber coverage. */
export const CORE_BLOCK_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.blockQuery
    ? [
        {
            chain,
            ...config.blockQuery,
        },
    ]
    : []);
export const CORE_BLOCK_QUERY_TEST_CHAINS = CORE_BLOCK_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getBalance vectors for chains whose upstream providers support historical balance queries at the pinned height. */
export const CORE_BALANCE_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.balanceQuery
    ? [
        {
            chain,
            ...config.balanceQuery,
        },
    ]
    : []);
export const CORE_BALANCE_QUERY_TEST_CHAINS = CORE_BALANCE_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getTokenBalance vectors for chains whose upstream providers support historical token balance queries at the pinned height. */
export const CORE_TOKEN_BALANCE_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.tokenBalanceQuery
    ? [
        {
            chain,
            ...config.tokenBalanceQuery,
        },
    ]
    : []);
export const CORE_TOKEN_BALANCE_QUERY_TEST_CHAINS = CORE_TOKEN_BALANCE_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getBlockByHash vectors for chains with verified archive-backed block lookups. */
export const CORE_BLOCK_HASH_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.blockHashQuery
    ? [
        {
            chain,
            ...config.blockHashQuery,
        },
    ]
    : []);
export const CORE_BLOCK_HASH_QUERY_TEST_CHAINS = CORE_BLOCK_HASH_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getBlockTransactions vectors — each entry loads the full transaction list for its chain's pinned block. */
export const CORE_BLOCK_TRANSACTIONS_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.blockTransactionsQuery
    ? [
        {
            chain,
            ...config.blockTransactionsQuery,
        },
    ]
    : []);
export const CORE_BLOCK_TRANSACTIONS_QUERY_TEST_CHAINS = CORE_BLOCK_TRANSACTIONS_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getTransfers vectors — 50-block window ending at each chain's pinned height for the reference address. */
export const CORE_TRANSFERS_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.transfersQuery
    ? [
        {
            chain,
            ...config.transfersQuery,
        },
    ]
    : []);
export const CORE_TRANSFERS_QUERY_TEST_CHAINS = CORE_TRANSFERS_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned simulateCall vectors — each entry runs balanceOf(EVM_REFERENCE_ADDRESS) against its chain's token at the pinned block. */
export const CORE_SIMULATE_CALL_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.simulateCallQuery
    ? [
        {
            chain,
            ...config.simulateCallQuery,
        },
    ]
    : []);
export const CORE_SIMULATE_CALL_QUERY_TEST_CHAINS = CORE_SIMULATE_CALL_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned traceTransaction vectors — reuses the same per-chain hashes as `receiptQuery`. */
export const CORE_TRACE_TRANSACTION_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.traceTransactionQuery
    ? [
        {
            chain,
            ...config.traceTransactionQuery,
        },
    ]
    : []);
export const CORE_TRACE_TRANSACTION_QUERY_TEST_CHAINS = CORE_TRACE_TRANSACTION_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getReceipt vectors — each hash is the first transaction from the chain's pinned block. */
export const CORE_RECEIPT_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.receiptQuery
    ? [
        {
            chain,
            ...config.receiptQuery,
        },
    ]
    : []);
export const CORE_RECEIPT_QUERY_TEST_CHAINS = CORE_RECEIPT_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getTransactionByHash vectors — reuses the same per-chain hashes as `receiptQuery`. */
export const CORE_TRANSACTION_BY_HASH_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.transactionByHashQuery
    ? [
        {
            chain,
            ...config.transactionByHashQuery,
        },
    ]
    : []);
export const CORE_TRANSACTION_BY_HASH_QUERY_TEST_CHAINS = CORE_TRANSACTION_BY_HASH_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned estimateGas vectors — each entry estimates a basic `to`-only transfer. */
export const CORE_ESTIMATE_GAS_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.estimateGasQuery
    ? [
        {
            chain,
            ...config.estimateGasQuery,
        },
    ]
    : []);
export const CORE_ESTIMATE_GAS_QUERY_TEST_CHAINS = CORE_ESTIMATE_GAS_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getCode vectors — each entry targets a well-known contract at the pinned height. */
export const CORE_CODE_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.codeQuery
    ? [
        {
            chain,
            ...config.codeQuery,
        },
    ]
    : []);
export const CORE_CODE_QUERY_TEST_CHAINS = CORE_CODE_QUERY_VECTORS.map((vector) => vector.chain);
/** Pinned getLogs vectors — each entry targets a known contract + Transfer topic in a pinned block window. */
export const CORE_LOGS_QUERY_VECTORS = CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.logsQuery
    ? [
        {
            chain,
            ...config.logsQuery,
        },
    ]
    : []);
export const CORE_LOGS_QUERY_TEST_CHAINS = CORE_LOGS_QUERY_VECTORS.map((vector) => vector.chain);
/** Per-chain balance vectors used by integration contract tests. */
export const CORE_BALANCE_TEST_VECTORS = Object.fromEntries(CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.balance ? [[chain, config.balance]] : []));
export const CORE_BALANCE_TEST_CHAINS = Object.keys(CORE_BALANCE_TEST_VECTORS);
/** Per-chain token balance vectors for chains where token-balance lookups are supported. */
export const CORE_TOKEN_BALANCE_TEST_VECTORS = Object.fromEntries(CORE_CHAIN_TEST_ENTRIES.flatMap(([chain, config]) => config.tokenBalance ? [[chain, config.tokenBalance]] : []));
export const CORE_TOKEN_BALANCE_TEST_CHAINS = Object.keys(CORE_TOKEN_BALANCE_TEST_VECTORS);
